import clarity from "@microsoft/clarity";

const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

declare global {
  interface Window {
    clarity?: (...args: any[]) => any;
    __clarityInitialized?: boolean;
  }
}

/**
 * Initialize Microsoft Clarity
 * 
 * This function should be called once when the app loads (client-side only).
 * It checks if Clarity is already initialized to prevent duplicate initialization.
 * 
 * @returns void
 */
export function initClarity(): void {
  // Only run in browser
  if (typeof window === "undefined") {
    return;
  }

  // Check if project ID is configured
  if (!CLARITY_ID) {
    console.warn("Microsoft Clarity: NEXT_PUBLIC_CLARITY_PROJECT_ID is missing");
    return;
  }

  // Prevent duplicate initialization
  if (window.__clarityInitialized) {
    console.log("Microsoft Clarity: Already initialized");
    return;
  }

  try {
    clarity.init(CLARITY_ID);
    window.__clarityInitialized = true;
    console.log("Microsoft Clarity: Initialized successfully");
  } catch (error) {
    console.error("Microsoft Clarity: Initialization failed", error);
  }
}

/**
 * Update Microsoft Clarity consent settings
 * 
 * Call this function whenever the user updates their cookie preferences.
 * This is required for GDPR compliance and Clarity's new consent requirements (Oct 31, 2025).
 * 
 * For EEA/UK/CH users, Clarity will not fully collect analytics data unless consent is granted.
 * 
 * @param options - Consent options
 * @param options.analytics - Whether analytics cookies are allowed (required for Clarity)
 * @param options.ads - Whether advertising cookies are allowed (optional, defaults to false)
 * 
 * @example
 * // User accepts analytics cookies
 * updateClarityConsent({ analytics: true });
 * 
 * @example
 * // User rejects analytics cookies
 * updateClarityConsent({ analytics: false });
 * 
 * @example
 * // User accepts both analytics and ads
 * updateClarityConsent({ analytics: true, ads: true });
 */
export function updateClarityConsent({
  analytics,
  ads = false,
}: {
  analytics: boolean;
  ads?: boolean;
}): void {
  // Only run in browser
  if (typeof window === "undefined") {
    return;
  }

  // Check if Clarity is loaded
  if (typeof window.clarity !== "function") {
    console.warn("Microsoft Clarity: Not initialized yet, cannot update consent");
    return;
  }

  try {
    // Send consent signal to Clarity
    // This uses Clarity's Consent Mode v2 API
    window.clarity("consent", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: ads ? "granted" : "denied",
    });

    console.log("Microsoft Clarity: Consent updated", {
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: ads ? "granted" : "denied",
    });
  } catch (error) {
    console.error("Microsoft Clarity: Failed to update consent", error);
  }
}

/**
 * Get the current Clarity initialization status
 * 
 * @returns boolean - True if Clarity is initialized
 */
export function isClarityInitialized(): boolean {
  return typeof window !== "undefined" && window.__clarityInitialized === true;
}

/**
 * Check if Clarity is available (loaded and ready)
 * 
 * @returns boolean - True if Clarity function is available
 */
export function isClarityAvailable(): boolean {
  return (
    typeof window !== "undefined" && typeof window.clarity === "function"
  );
}

/**
 * Identify user in Microsoft Clarity
 * 
 * This function tags the Clarity session with the user's store ID,
 * allowing you to filter and search for specific users in the Clarity dashboard.
 * 
 * @param storeId - The store ID to associate with this Clarity session
 * 
 * @example
 * // After user login, identify them in Clarity
 * identifyUser("163");
 * // Now you can search Clarity for "Store 163"
 */
export function identifyUser(storeId: string | number): void {
  // Only run in browser
  if (typeof window === "undefined") return;

  // Check if storeId is valid
  if (!storeId) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Microsoft Clarity: Invalid store ID provided");
    }
    return;
  }

  // Check if Clarity is loaded
  if (typeof window.clarity !== "function") {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Microsoft Clarity: Not initialized yet, cannot identify user"
      );
    }
    return;
  }

  try {
    const storeIdString = String(storeId);

    // Identify API:
    // window.clarity("identify", customId, customSessionId?, customPageId?, friendlyName?)
    const identifyResult = window.clarity("identify", storeIdString);

    // Set custom tag for filtering:
    // window.clarity("set", key, value)
    window.clarity("set", "store_id", storeIdString);

    // Best-effort debug logging for local verification
    if (process.env.NODE_ENV === "development") {
      console.log("Microsoft Clarity: User identified", { store_id: storeIdString });
      if (identifyResult?.then) {
        identifyResult
          .then((res: any) => {
            console.log("Microsoft Clarity: identify() resolved", res);
          })
          .catch((e: any) => {
            console.warn("Microsoft Clarity: identify() rejected", e);
          });
      }
    }
  } catch (error) {
    console.error("Microsoft Clarity: Failed to identify user", error);
  }
}
